import { useEffect, useState, type ReactNode } from 'react';
import { Pestanas } from '../components/Pestanas';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { TopBar } from '../components/TopBar';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { confirmar } from '../components/Dialogo';
import { rolEtiqueta } from '../lib/roles';
import { MasMenu } from '../components/MasMenu';
import { conPin, pinHeader } from '../lib/pin';
import { telefonoLegible } from '../lib/phone';
import { Accesos } from './AdminAccesos';
import { Permisos } from './AdminPermisos';

/**
 * Administración — el panel de solo-admins.
 *
 * Lo que antes era "tocar la base a mano" (dar de alta una cuenta, corregir un
 * precio, agregar un premio de puntos) ahora son pantallas: Cuentas,
 * Procedimientos, Paquetes, Farmacia, Productos propios (lo que se vende en la
 * app y no está en el catálogo, como el libro), Premios de puntos y Clínicas.
 *
 * CÓMO SE VE CADA PRODUCTO EN LA APP DE PACIENTES (0.28.0): se edita aquí, en el
 * diálogo "Editar" de cada producto — mostrar u ocultar, foto, descripción,
 * destacado y cómo se entrega (se agenda cita / se recoge / envío). Antes vivía
 * en App de pacientes → Tienda como tarjetas sueltas debajo del catálogo.
 *
 * Admin de CLÍNICA ve y toca lo suyo. Admin GENERAL (tenant corporativo) además
 * elige clínica y administra la lista de clínicas.
 */

interface Clinica { tenant_id: string; nombre: string; domicilio: string | null; activo: boolean; usuarios: number; pacientes: number }

const inputCss: React.CSSProperties = { padding: '7px 10px', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'inherit', fontSize: 13 };
const money = (n: number) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n || 0);

// Cómo se LEE cada rol en pantalla: lib/roles.ts (lo comparte Comisiones).

function Aviso({ texto, tipo }: { texto: string; tipo: 'ok' | 'error' }) {
  return <div role="status" style={{ padding: '8px 12px', borderRadius: 8, fontSize: 13, margin: '8px 0',
    background: tipo === 'ok' ? 'var(--success-bg, #d1fae5)' : 'var(--warning-bg, #fef3c7)',
    color: tipo === 'ok' ? '#065f46' : '#92400e' }}>{texto}</div>;
}

type TabAdmin = 'cuentas' | 'procedimientos' | 'paquetes' | 'farmacia' | 'propios' | 'premios' | 'clinicas' | 'preferencias' | 'permisos' | 'accesos';
const TABS: [TabAdmin, string][] = [
  ['cuentas', 'Cuentas'], ['procedimientos', 'Procedimientos'], ['paquetes', 'Paquetes'], ['farmacia', 'Farmacia'],
  ['propios', 'Productos propios'], ['premios', 'Premios de puntos'], ['clinicas', 'Clínicas'], ['preferencias', 'Preferencias'],
  ['permisos', 'Permisos'], ['accesos', 'Accesos'],
];
// `?tab=` para llegar directo (App de pacientes → Tienda manda aquí). "tienda" era el nombre viejo de Farmacia.
const tabDeUrl = (t: string | null): TabAdmin => (t === 'tienda' ? 'farmacia' : (TABS.find(([k]) => k === t)?.[0] || 'cuentas'));

export function Admin() {
  const { user } = useAuth();
  const esGeneral = user?.scope === 'all';
  const [params, setParams] = useSearchParams();
  const [tab, setTabState] = useState<TabAdmin>(tabDeUrl(params.get('tab')));
  const [clinicas, setClinicas] = useState<Clinica[]>([]);
  const [tenantSel, setTenantSel] = useState<string>('');

  useEffect(() => {
    api<{ clinicas: Clinica[] }>('panel/clinicas').then((d) => {
      setClinicas(d.clinicas);
      if (!tenantSel && d.clinicas.length) setTenantSel(d.clinicas.find((c) => c.activo)?.tenant_id || d.clinicas[0].tenant_id);
    }).catch(() => {});
  }, []);
  useEffect(() => { const t = tabDeUrl(params.get('tab')); if (params.get('tab') && t !== tab) setTabState(t); }, [params]);
  const setTab = (t: TabAdmin) => { setTabState(t); setParams(t === 'cuentas' ? {} : { tab: t }, { replace: true }); };

  const qs = esGeneral && tenantSel ? `?tenant=${tenantSel}` : '';

  return (
    <div>
      <TopBar />
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '18px 16px 48px' }}>
        <h1 style={{ margin: '4px 2px 6px', fontSize: 24, fontWeight: 300, color: 'var(--gold-dark)' }}>Administración</h1>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Cuentas, catálogos, lo que se vende en la app, clínicas, preferencias, qué sección abre cada rol y la bitácora de accesos — sin tocar la base.
          {esGeneral ? ' Eres admin general: elige la clínica que administras.' : ' Administras tu clínica.'}
        </p>

        {esGeneral && tab !== 'clinicas' && (
          <div style={{ margin: '0 2px 12px' }}>
            <label className="muted" style={{ fontSize: 12, marginRight: 8 }}>Clínica:</label>
            <select value={tenantSel} onChange={(e) => setTenantSel(e.target.value)} style={inputCss}>
              {clinicas.map((c) => <option key={c.tenant_id} value={c.tenant_id}>{c.nombre}{c.activo ? '' : ' (inactiva)'}</option>)}
            </select>
          </div>
        )}

        <Pestanas items={TABS} valor={tab} onCambio={setTab} etiqueta="Secciones de Administración" />

        {tab === 'cuentas' && <Cuentas qs={qs} tenantSel={tenantSel} />}
        {tab === 'procedimientos' && <Procedimientos qs={qs} tenantSel={tenantSel} />}
        {tab === 'paquetes' && <Paquetes qs={qs} tenantSel={tenantSel} />}
        {tab === 'farmacia' && <Farmacia qs={qs} tenantSel={tenantSel} />}
        {tab === 'propios' && <Propios qs={qs} tenantSel={tenantSel} />}
        {tab === 'premios' && <Premios qs={qs} tenantSel={tenantSel} />}
        {tab === 'preferencias' && <PreferenciasClinica qs={qs} tenantSel={tenantSel} />}
        {tab === 'permisos' && <Permisos qs={qs} tenantSel={tenantSel} />}
        {tab === 'accesos' && <Accesos qs={qs} tenantSel={tenantSel} />}
        {tab === 'clinicas' && <Clinicas esGeneral={esGeneral} onCambio={() => api<{ clinicas: Clinica[] }>('panel/clinicas').then((d) => setClinicas(d.clinicas)).catch(() => {})} />}
      </div>
    </div>
  );
}

// ============================================================== CUENTAS
interface Usuario { user_id: string; nombre: string; rol: string; activo: boolean; email: string | null; telefono?: string | null; ultimo_acceso: string | null; tenant_nombre: string; clinico?: boolean; tiene_contrasena?: boolean }
// El teléfono de una cuenta (opcional): la misma ayuda que en DraIA y recepción. El servidor lo valida (api/src/lib/cuentas.ts).
const AYUDA_TEL = '10 dígitos. Si es de otro país, empieza con + y su lada (p. ej. +1 305 555 1234).';
const ROLES_CLINICOS = ['medico', 'cosmetologa'];

function Cuentas({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ usuarios: Usuario[]; roles: string[] } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [form, setForm] = useState({ nombre: '', email: '', password: '', rol: 'asesora', telefono: '' });
  const [busy, setBusy] = useState(false);
  const [editandoCuenta, setEditandoCuenta] = useState<Usuario | null>(null);
  // La alta va plegada detrás de "+ Nueva cuenta": casi siempre se viene a revisar o editar, no a dar de alta.
  const [altaAbierta, setAltaAbierta] = useState(false);

  const load = () => api<{ usuarios: Usuario[]; roles: string[] }>('panel/usuarios' + qs).then(setD).catch((e) => setMsg({ t: e.message, tipo: 'error' }));
  // Quién tiene PIN (0.47.0): sólo sí/no y si está bloqueado. Sin la tabla todavía, nadie (no es un error).
  const [pins, setPins] = useState<Record<string, { bloqueadoHasta: string | null }>>({});
  const navigate = useNavigate();
  const loadPins = () => api<{ cuentas: { user_id: string; bloqueadoHasta: string | null }[] }>('cuenta/pin?equipo=1' + (qs ? '&' + qs.replace(/^\?/, '') : ''))
    .then((r) => setPins(Object.fromEntries((r.cuentas || []).map((x) => [x.user_id, { bloqueadoHasta: x.bloqueadoHasta }])))).catch(() => setPins({}));
  useEffect(() => { setD(null); load(); loadPins(); }, [qs, tenantSel]);
  // Reiniciar el PIN de otra persona: con el PIN de quien lo hace. Esa persona pone uno nuevo en "Mi cuenta".
  const reiniciarPin = async (u: Usuario) => {
    if (!(await confirmar({ titulo: `¿Reiniciar el PIN de ${u.nombre}?`, peligro: true, confirmar: 'Siguiente',
      mensaje: 'Se borra su PIN y tendrá que poner uno nuevo en "Mi cuenta" antes de confirmar algo delicado. Úsalo si lo olvidó o quedó bloqueado.' }))) return;
    const r = await conPin(`Reiniciar el PIN de ${u.nombre}`, (pin) => api<any>(`cuenta/pin/${u.user_id}` + qs, { method: 'DELETE', headers: pinHeader(pin) }),
      { irAMiCuenta: () => navigate('/mi-cuenta'), errorTitulo: 'No se pudo reiniciar' });
    if (r) { setMsg({ t: `Listo: ${u.nombre} ya no tiene PIN; pondrá uno nuevo en "Mi cuenta".`, tipo: 'ok' }); loadPins(); }
  };

  const crear = async () => {
    setBusy(true); setMsg(null);
    try {
      await api('panel/usuarios' + qs, { method: 'POST', body: JSON.stringify(form) });
      setMsg({ t: `Cuenta creada: ${form.nombre} (${rolEtiqueta(form.rol)}). Ya puede entrar con su email y contraseña.`, tipo: 'ok' });
      setForm({ nombre: '', email: '', password: '', rol: 'asesora', telefono: '' });
      setAltaAbierta(false);
      load();
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
    setBusy(false);
  };

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/usuarios/${id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  // Desactivar saca a la persona del HUB al momento: se pregunta (antes era un botón suelto en cada fila).
  const desactivar = async (u: Usuario) => {
    if (!(await confirmar({ titulo: `¿Desactivar la cuenta de ${u.nombre}?`, peligro: true, confirmar: 'Desactivar',
      mensaje: 'Ya no podrá entrar al HUB. No se borra nada: se puede reactivar cuando quieras.' }))) return;
    await editar(u.user_id, { activo: false });
  };
  // "Atiende pacientes" (equipo clínico, lib/equipo-clinico.ts en el API): para una cuenta que no es del equipo médico
  // ni de cosmetología (la doctora dueña con su cuenta de admin). Sale en "Quién lo realiza" de DraIA y comisiona.
  const atiende = async (u: Usuario, si: boolean) => {
    setMsg(null);
    // La casilla cambia al momento (y si el servidor no acepta, la lista se vuelve a leer tal cual está).
    setD((x) => (x ? { ...x, usuarios: x.usuarios.map((y) => (y.user_id === u.user_id ? { ...y, clinico: si } : y)) } : x));
    try {
      await api('panel/equipo-clinico' + qs, { method: 'PUT', body: JSON.stringify({ user_id: u.user_id, atiende: si }) });
      setMsg({ t: si ? `${u.nombre} también atiende pacientes: sale en "Quién lo realiza" y comisiona procedimientos.` : `${u.nombre} ya no sale como parte del equipo que atiende pacientes.`, tipo: 'ok' });
      load();
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); load(); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 2px 12px' }}>
        <span className="muted" style={{ fontSize: 13 }}>{d ? `${d.usuarios.filter((u) => u.activo).length} cuentas activas${d.usuarios.some((u) => !u.activo) ? ` · ${d.usuarios.filter((u) => !u.activo).length} desactivadas` : ''}` : ''}</span>
        {!altaAbierta && <button className="btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setAltaAbierta(true)} data-nueva="cuenta">+ Nueva cuenta</button>}
      </div>
      {altaAbierta && <div className="card" style={{ padding: 16, marginBottom: 16 }} data-alta="cuenta">
        <div style={{ fontWeight: 600, marginBottom: 10 }}>Nueva cuenta</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={inputCss} />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={inputCss} />
          <input placeholder="Contraseña (mín. 8)" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} style={inputCss} />
          <input placeholder="Teléfono (opcional): 55 1234 5678" type="tel" inputMode="tel" autoComplete="off" aria-label="Teléfono (opcional)" value={form.telefono}
            onChange={(e) => setForm({ ...form, telefono: e.target.value })} style={inputCss} data-alta-telefono />
          <select value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })} style={inputCss}>
            {(d?.roles || ['admin', 'medico', 'cosmetologa', 'recepcion', 'asesora', 'marketing']).map((r) => <option key={r} value={r}>{rolEtiqueta(r)}</option>)}
          </select>
          <button className="btn-primary" onClick={crear} disabled={busy || !form.nombre || !form.email || form.password.length < 8}>
            {busy ? 'Creando…' : 'Crear cuenta'}</button>
          <button className="btn-ghost" onClick={() => setAltaAbierta(false)} disabled={busy}>Cancelar</button>
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>Teléfono: {AYUDA_TEL}</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          <strong>marketing</strong> solo ve la Galería (sin nombres de pacientes). <strong>cosmetología</strong>, recepción y el equipo
          médico ven el Resumen y sus comisiones (sólo las suyas). Si el rol marca error de valor inválido,
          primero aplica migraciones en <Link to="/config" style={{ color: 'var(--gold-dark)' }}>Reglas y datos → Salud de datos</Link>.
        </div>
      </div>}
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando cuentas…</div>}
      {d && (
        <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Nombre', 'Email y teléfono', 'Rol', 'Último acceso', 'Estado', ''].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {d.usuarios.map((u) => (
                <tr key={u.user_id} style={{ opacity: u.activo ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600 }} data-cuenta={u.nombre}>{u.nombre}
                    {u.clinico && !ROLES_CLINICOS.includes(u.rol) && <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--gold-dark)' }}>atiende pacientes</span>}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{u.email || '—'}
                    {u.telefono && <span className="muted" style={{ display: 'block', fontSize: 12, whiteSpace: 'nowrap' }} data-telefono-cuenta={u.nombre}>{telefonoLegible(u.telefono)}</span>}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <select value={u.rol} onChange={(e) => editar(u.user_id, { rol: e.target.value })} style={{ ...inputCss, padding: '4px 6px' }} aria-label={`Rol de ${u.nombre}`}>
                      {(d.roles).map((r) => <option key={r} value={r}>{rolEtiqueta(r)}</option>)}
                    </select>
                    {u.activo && !ROLES_CLINICOS.includes(u.rol) && u.rol !== 'marketing' && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, marginTop: 4, cursor: 'pointer', whiteSpace: 'nowrap' }} className="muted"
                        title={'También atiende pacientes: sale en "Quién lo realiza" (DraIA y recepción) y comisiona procedimientos, como el equipo médico.'}>
                        <input type="checkbox" checked={!!u.clinico} onChange={(e) => atiende(u, e.target.checked)} data-atiende={u.nombre} style={{ width: 'auto' }} /> atiende pacientes
                      </label>
                    )}</td>
                  <td className="muted" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    {u.ultimo_acceso ? new Date(u.ultimo_acceso).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : 'nunca'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{u.activo ? 'activa' : 'desactivada'}
                    <span className="muted" style={{ display: 'block', fontSize: 11 }} data-pin-cuenta={pins[u.user_id] ? (pins[u.user_id].bloqueadoHasta ? 'bloqueado' : 'tiene') : 'sin'}>
                      {pins[u.user_id] ? (pins[u.user_id].bloqueadoHasta ? 'PIN bloqueado' : 'con PIN') : 'sin PIN'}</span>
                    {u.activo && u.tiene_contrasena === false && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--warning)', whiteSpace: 'nowrap' }} data-sin-contrasena={u.nombre}
                        title="No puede entrar a ninguna app hasta que se le ponga una contraseña (Editar).">sin contraseña: no puede entrar</span>)}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }}
                        onClick={() => setEditandoCuenta(u)}>Editar</button>
                      <MasMenu etiqueta="" ariaLabel={`Más acciones de ${u.nombre}`} datos={`cuenta:${u.nombre}`} items={[
                        pins[u.user_id] && { etiqueta: 'Reiniciar su PIN…', onClick: () => reiniciarPin(u), accion: 'reiniciar-pin' },
                        u.activo ? { etiqueta: 'Desactivar cuenta…', peligro: true, onClick: () => desactivar(u), accion: 'desactivar' }
                          : { etiqueta: 'Reactivar cuenta', onClick: () => editar(u.user_id, { activo: true }), accion: 'reactivar' },
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editandoCuenta && (
        <ModalEditar
          titulo={`Editar cuenta: ${editandoCuenta.nombre}`}
          campos={[
            { key: 'nombre', label: 'Nombre', valor: editandoCuenta.nombre },
            { key: 'email', label: 'Email (con el que entra)', valor: editandoCuenta.email || '' },
            { key: 'telefono', label: `Teléfono (opcional; vacío = sin teléfono). ${AYUDA_TEL}`, valor: telefonoLegible(editandoCuenta.telefono), tipo: 'tel' },
            { key: 'rol', label: 'Rol', valor: editandoCuenta.rol, opciones: d?.roles || [], etiqueta: rolEtiqueta },
            { key: 'password', label: editandoCuenta.tiene_contrasena === false
                ? 'Contraseña (no tiene: sin ella no puede entrar; mín. 8). Después la cambia en Mi cuenta.'
                : 'Nueva contraseña (dejar vacío para no cambiarla; mín. 8). Vale para DTB HUB, Dra. IA y recepción.', valor: '' },
          ]}
          onGuardar={async (v) => {
            const u = editandoCuenta;
            const cambios: Record<string, string> = {};
            if (v.nombre.trim() && v.nombre.trim() !== u.nombre) cambios.nombre = v.nombre.trim();
            if (v.email.trim() && v.email.trim() !== (u.email || '')) cambios.email = v.email.trim();
            if (v.rol && v.rol !== u.rol) cambios.rol = v.rol;
            // El teléfono: sólo si cambió (el servidor lo valida y dice en palabras qué está mal).
            if (v.telefono.trim() !== telefonoLegible(u.telefono)) cambios.telefono = v.telefono.trim();
            if (v.password) {
              if (v.password.length < 8) throw new Error('La contraseña debe tener al menos 8 caracteres.');
              cambios.password = v.password;
            }
            if (!Object.keys(cambios).length) return;
            await api(`panel/usuarios/${u.user_id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) });
            setMsg({ t: `Cuenta de ${v.nombre.trim() || u.nombre} actualizada.`, tipo: 'ok' });
            load();
          }}
          onCerrar={() => setEditandoCuenta(null)}
        />
      )}
    </div>
  );
}

// ================================================ EN LA APP DE PACIENTES
// La sección del diálogo de cada producto que dice cómo sale en la tienda de la app Safevity Rewards. Viaja
// como `app: {...}` (sólo lo que cambió) en el PUT del producto y la foto por `POST panel/app-foto`.
type TipoProducto = 'tratamiento' | 'paquete' | 'farmacia' | 'libro' | 'otro';
interface AppInfo { item_id: string | null; en_app: boolean; descripcion: string | null; destacado: boolean; entrega: string; foto_url: string | null; con_foto?: boolean }
interface EstadoApp { en_app: boolean; descripcion: string; destacado: boolean; entrega: string; foto_url: string | null; foto_nueva: string | null; quitar_foto: boolean }
interface GuardadoApp { cambios: Record<string, unknown> | null; foto: string | null }

const ENTREGA_DEFAULT: Record<TipoProducto, string> = { tratamiento: 'agendar', paquete: 'agendar', farmacia: 'recoger', libro: 'recoger', otro: 'recoger' };
const ENTREGAS: { v: string; t: string; sub: string }[] = [
  { v: 'agendar', t: 'Se agenda cita', sub: 'Al pagar, la app ofrece agendar por WhatsApp.' },
  { v: 'recoger', t: 'Se recoge en la clínica', sub: 'Recepción lo entrega y lo marca como entregado.' },
  { v: 'envio', t: 'Envío a domicilio', sub: 'La app no pide dirección: la clínica coordina el envío después del pago.' },
];
const ENTREGA_CORTA: Record<string, string> = { agendar: 'se agenda cita', recoger: 'se recoge', envio: 'envío' };

const estadoApp = (a: AppInfo | undefined, tipo: TipoProducto): EstadoApp => ({
  en_app: a ? a.en_app : true, descripcion: a?.descripcion || '', destacado: !!a?.destacado,
  entrega: a?.entrega || ENTREGA_DEFAULT[tipo], foto_url: a?.foto_url || null, foto_nueva: null, quitar_foto: false,
});
/** Sólo lo que cambió (el servidor pone los defaults a lo que no viaja). */
function cambiosApp(ini: EstadoApp, fin: EstadoApp): Record<string, unknown> | null {
  const c: Record<string, unknown> = {};
  if (fin.en_app !== ini.en_app) c.en_app = fin.en_app;
  if (fin.descripcion.trim() !== ini.descripcion.trim()) c.descripcion = fin.descripcion.trim() || null;
  if (fin.destacado !== ini.destacado) c.destacado = fin.destacado;
  if (fin.entrega !== ini.entrega) c.entrega = fin.entrega;
  if (fin.quitar_foto && !fin.foto_nueva) c.quitar_foto = true;
  return Object.keys(c).length ? c : null;
}
/** La foto se reduce AQUÍ (1200 px, JPEG): al servidor no viaja la de 12 MB del celular. */
function reducirFoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image(); const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1200; const k = Math.min(1, max / Math.max(img.width, img.height));
      const cv = document.createElement('canvas'); cv.width = Math.max(1, Math.round(img.width * k)); cv.height = Math.max(1, Math.round(img.height * k));
      const g = cv.getContext('2d')!; g.fillStyle = '#fff'; g.fillRect(0, 0, cv.width, cv.height); g.drawImage(img, 0, 0, cv.width, cv.height);
      URL.revokeObjectURL(url); resolve(cv.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('No se pudo leer esa imagen (usa JPG o PNG).')); };
    img.src = url;
  });
}
const pistaApp = (a: AppInfo | undefined, tipo: TipoProducto) => a ? [a.con_foto ? 'con foto' : '', a.destacado ? 'destacado' : '', a.entrega && a.entrega !== ENTREGA_DEFAULT[tipo] ? ENTREGA_CORTA[a.entrega] : ''].filter(Boolean).join(' · ') : '';

function SeccionApp({ valor, onChange, conPrecio, fallback, almacen, propio }: {
  valor: EstadoApp; onChange: (v: EstadoApp) => void; conPrecio: boolean; fallback?: string | null; almacen?: boolean; propio?: boolean;
}) {
  const [err, setErr] = useState('');
  const foto = valor.foto_nueva || (valor.quitar_foto ? null : valor.foto_url);
  const set = (p: Partial<EstadoApp>) => onChange({ ...valor, ...p });
  const lbl: React.CSSProperties = { fontSize: 12, display: 'block', marginBottom: 4 };
  return (
    <div data-seccion-app style={{ marginTop: 16, paddingTop: 14, borderTop: '2px solid var(--gold-medium)', display: 'grid', gap: 12 }}>
      <div>
        <div style={{ fontWeight: 700, color: 'var(--gold-dark)', fontSize: 14 }}>En la app de pacientes</div>
        <div className="muted" style={{ fontSize: 12, lineHeight: 1.45 }}>Así sale en la tienda de la app Safevity Rewards. {propio ? 'Aquí manda el precio de arriba.' : 'El precio que se cobra siempre es el del catálogo.'}</div>
      </div>
      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
        <input type="checkbox" data-campo="en_app" checked={valor.en_app} onChange={(e) => set({ en_app: e.target.checked })} style={{ width: 18, height: 18, accentColor: '#8b7355' }} /> Mostrar en la app
      </label>
      {valor.en_app && !conPrecio && <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: -6 }}>Sin precio no sale en la app aunque esté marcado: ponle precio para que aparezca.</div>}
      <div>
        <span className="muted" style={lbl}>Foto</span>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div data-foto-preview={foto ? 'si' : 'no'} style={{ width: 132, aspectRatio: '4 / 3', borderRadius: 12, border: '1px solid var(--border)', background: foto ? `center / cover no-repeat url("${foto}")` : 'var(--bg-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, color: 'var(--ink-soft)', flex: '0 0 auto' }}>{foto ? '' : 'Sin foto'}</div>
          <div style={{ display: 'grid', gap: 6, justifyItems: 'start' }}>
            <label className="btn-ghost" style={{ padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', borderRadius: 10 }}>{foto ? 'Cambiar foto' : 'Subir foto'}
              <input type="file" accept="image/*" hidden data-campo="foto" onChange={async (e) => {
                const f = e.target.files && e.target.files[0]; e.target.value = ''; if (!f) return;
                setErr(''); try { set({ foto_nueva: await reducirFoto(f), quitar_foto: false }); } catch (x: any) { setErr(x.message); }
              }} /></label>
            {foto && <button type="button" className="btn-ghost" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => set({ foto_nueva: null, quitar_foto: true })}>Quitar foto</button>}
            {valor.foto_nueva && <span className="muted" style={{ fontSize: 11.5 }}>Se sube al guardar.</span>}
          </div>
        </div>
        {almacen === false && <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 6 }}>Para guardar fotos falta AZURE_STORAGE_CONNECTION_STRING en los App Settings del HUB (el mismo valor que DraIA).</div>}
        {err && <div style={{ fontSize: 12, color: 'var(--warning)', marginTop: 6 }}>{err}</div>}
      </div>
      <div>
        <label className="muted" style={lbl} htmlFor="app-descripcion">Descripción para la app</label>
        <textarea id="app-descripcion" data-campo="descripcion" value={valor.descripcion} maxLength={1000} rows={3} onChange={(e) => set({ descripcion: e.target.value })}
          placeholder={fallback ? `Si la dejas vacía se usa la del catálogo: «${fallback.slice(0, 140)}${fallback.length > 140 ? '…' : ''}»` : 'Qué es, para qué sirve, qué incluye.'}
          style={{ ...inputCss, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
      </div>
      <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 13.5, cursor: 'pointer' }}>
        <input type="checkbox" data-campo="destacado" checked={valor.destacado} onChange={(e) => set({ destacado: e.target.checked })} style={{ width: 17, height: 17, accentColor: '#8b7355' }} /> Destacado <span className="muted" style={{ fontSize: 12 }}>(sale hasta arriba en la tienda)</span>
      </label>
      <div role="radiogroup" aria-label="Cómo se entrega" data-campo="entrega">
        <span className="muted" style={lbl}>Cómo se entrega</span>
        <div style={{ display: 'grid', gap: 6 }}>
          {ENTREGAS.map((o) => (
            <label key={o.v} data-entrega={o.v} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '7px 10px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${valor.entrega === o.v ? 'var(--gold)' : 'var(--border)'}`, background: valor.entrega === o.v ? 'var(--gold-light)' : '#fff' }}>
              <input type="radio" name="entrega-app" value={o.v} checked={valor.entrega === o.v} onChange={() => set({ entrega: o.v })} style={{ marginTop: 3, accentColor: '#8b7355' }} />
              <span style={{ minWidth: 0 }}><strong style={{ fontSize: 13 }}>{o.t}</strong><span className="muted" style={{ display: 'block', fontSize: 11.5 }}>{o.sub}</span></span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Sube la foto nueva (si la hay) después de guardar lo demás. Si falla, lo demás ya quedó y se dice. */
async function subirFotoApp(qs: string, destino: { tipo: string; ref_id: string } | { item_id: string }, foto: string | null) {
  if (!foto) return;
  try { await api('panel/app-foto' + qs, { method: 'POST', body: JSON.stringify({ ...destino, dataUrl: foto }) }); }
  catch (e: any) { throw new Error(`Se guardó lo demás, pero la foto no: ${e.message}`); }
}

// ======================================================= PROCEDIMIENTOS
interface Proc { treatment_id: string; codigo: string; nombre: string; zona: string | null; costo_base: number | null; activo: boolean; escalones: number; descripcion: string | null; con_precio?: boolean; en_app?: boolean; app?: AppInfo }
interface Seccion { seccion: boolean; almacen?: boolean }
const NUEVO_NOTA = 'Ya quedó dado de alta. Aquí completas cómo se ve en la app (opcional).';

function Procedimientos({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ procedimientos: Proc[]; app?: Seccion } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [filtro, setFiltro] = useState('');
  const [form, setForm] = useState({ nombre: '', costo_base: '', en_app: true });
  const [editando, setEditando] = useState<{ fila: Proc; nuevo?: boolean } | null>(null);
  const [alta, setAlta] = useState(false);

  const load = () => api<{ procedimientos: Proc[]; app?: Seccion }>('panel/procedimientos' + qs).then((x) => { setD(x); return x; }).catch((e) => { setMsg({ t: e.message, tipo: 'error' }); return null; });
  useEffect(() => { setD(null); load(); }, [qs, tenantSel]);

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/procedimientos/${id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const crear = async () => {
    setMsg(null);
    try {
      const r = await api<{ creado: { treatment_id: string } }>('panel/procedimientos' + qs, { method: 'POST', body: JSON.stringify({ nombre: form.nombre, costo_base: form.costo_base ? Number(form.costo_base) : null, en_app: form.en_app }) });
      setForm({ nombre: '', costo_base: '', en_app: true }); setAlta(false);
      const x = await load(); const fila = x?.procedimientos.find((p) => p.treatment_id === r.creado?.treatment_id);
      if (fila) setEditando({ fila, nuevo: true });
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };

  const visibles = (d?.procedimientos || []).filter((p) => !filtro || p.nombre.toLowerCase().includes(filtro.toLowerCase()));
  const f = editando?.fila;

  return (
    <div>
      <BarraCatalogo datos="procedimientos" filtro={filtro} setFiltro={setFiltro} nuevo="+ Nuevo procedimiento" abierta={alta} setAbierta={setAlta}
        nota={<>El precio editable aquí es el <strong>precio base</strong> (unidad mínima). Los escalones por zona/paquete (precios_detalle) se muestran como dato; su edición fina viene después.
          {' '}<strong>En la app</strong>: lo que tiene precio sale solo en la tienda de la app de pacientes (con sus escalones). Foto, descripción, destacado y cómo se entrega: botón <strong>Editar</strong> de cada fila.</>}>
        <div style={btnAltaCss}>
          <input placeholder="Nombre del procedimiento" aria-label="Nombre del procedimiento" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={{ ...inputCss, flex: '1 1 220px', minWidth: 0 }} autoFocus />
          <input placeholder="Precio base (opcional)" type="number" value={form.costo_base} onChange={(e) => setForm({ ...form, costo_base: e.target.value })} style={{ ...inputCss, width: 150 }} />
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Sale en la tienda de la app de pacientes (si tiene precio)"><input type="checkbox" checked={form.en_app} onChange={(e) => setForm({ ...form, en_app: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#8b7355' }} /> Mostrar en la app</label>
          <button className="btn-primary" onClick={crear} disabled={!form.nombre.trim()}>Agregar</button>
          <button className="btn-ghost" onClick={() => setAlta(false)}>Cancelar</button>
        </div>
      </BarraCatalogo>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando catálogo…</div>}
      {d && <FilaCatalogo
        filas={visibles.map((p) => ({
          id: p.treatment_id, principal: p.nombre, activo: p.activo,
          extras: [p.codigo, p.zona || '—', p.escalones ? `${p.escalones} escalones` : 'sin escalones'],
          precio: p.costo_base,
          app: p.en_app === undefined ? undefined : { en_app: !!p.en_app, con_precio: !!p.con_precio, pista: pistaApp(p.app, 'tratamiento') },
        }))}
        totales={`${visibles.length} de ${d.procedimientos.length}`}
        onApp={(id, v) => editar(id, { en_app: v })} seccionApp={d.app?.seccion}
        onNombre={(id, v) => editar(id, { nombre: v })}
        onPrecio={(id, v) => editar(id, { costo_base: v })}
        onActivo={(id, v) => editar(id, { activo: v })}
        onEditarFila={(id) => { const fila = d.procedimientos.find((p) => p.treatment_id === id); if (fila) setEditando({ fila }); }}
      />}
      {f && editando && (
        <ModalEditar titulo={`${editando.nuevo ? 'Nuevo' : 'Editar'}: ${f.nombre}`} nota={editando.nuevo ? NUEVO_NOTA : undefined} cancelar={editando.nuevo ? 'Ahora no' : undefined}
          onCerrar={() => setEditando(null)}
          campos={[
            { key: 'nombre', label: 'Nombre', valor: f.nombre },
            { key: 'costo_base', label: 'Precio base (MXN)', valor: f.costo_base == null ? '' : String(f.costo_base), tipo: 'number' },
            { key: 'descripcion', label: 'Descripción (catálogo)', valor: f.descripcion || '', tipo: 'textarea' },
          ]}
          app={{ tipo: 'tratamiento', info: f.app, almacen: d?.app?.almacen,
            // Con escalones con precio, sale aunque el precio base esté vacío.
            conPrecio: (v) => (!!f.con_precio && !(Number(f.costo_base) > 0)) || Number(v.costo_base) > 0,
            fallback: (v) => v.descripcion || null }}
          onGuardar={async (v, app) => {
            await api(`panel/procedimientos/${f.treatment_id}` + qs, { method: 'PUT', body: JSON.stringify({
              nombre: v.nombre, costo_base: v.costo_base === '' ? null : Number(v.costo_base), descripcion: v.descripcion,
              ...(app.cambios ? { app: app.cambios } : {}),
            }) });
            try { await subirFotoApp(qs, { tipo: 'tratamiento', ref_id: f.treatment_id }, app.foto); } finally { load(); }
            setMsg({ t: `Guardado: ${v.nombre}.`, tipo: 'ok' });
          }} />
      )}
    </div>
  );
}

// ============================================================ PAQUETES
interface Paquete { package_id: string; nombre: string; descripcion: string | null; sesiones: number; precio: number | null; activo: boolean; con_precio: boolean; en_app: boolean; app?: AppInfo }

function Paquetes({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ paquetes: Paquete[]; app?: Seccion; disponible?: boolean; motivo?: string } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [filtro, setFiltro] = useState('');
  const [form, setForm] = useState({ nombre: '', sesiones: '1', precio: '', en_app: true });
  const [editando, setEditando] = useState<{ fila: Paquete; nuevo?: boolean } | null>(null);
  const [alta, setAlta] = useState(false);

  const load = () => api<{ paquetes: Paquete[]; app?: Seccion; disponible?: boolean; motivo?: string }>('panel/paquetes' + qs).then((x) => { setD(x); return x; }).catch((e) => { setMsg({ t: e.message, tipo: 'error' }); return null; });
  useEffect(() => { setD(null); load(); }, [qs, tenantSel]);

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/paquetes/${id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const crear = async () => {
    setMsg(null);
    try {
      const r = await api<{ creado: { package_id: string } }>('panel/paquetes' + qs, { method: 'POST', body: JSON.stringify({ nombre: form.nombre, sesiones: Number(form.sesiones) || 1, precio: form.precio === '' ? 0 : Number(form.precio), en_app: form.en_app }) });
      setForm({ nombre: '', sesiones: '1', precio: '', en_app: true }); setAlta(false);
      const x = await load(); const fila = x?.paquetes.find((p) => p.package_id === r.creado?.package_id);
      if (fila) setEditando({ fila, nuevo: true });
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  if (d && d.disponible === false) return <div className="card" style={{ padding: 16 }}><span className="muted" style={{ fontSize: 14 }}>{d.motivo}</span></div>;

  const visibles = (d?.paquetes || []).filter((p) => !filtro || p.nombre.toLowerCase().includes(filtro.toLowerCase()));
  const f = editando?.fila;
  return (
    <div>
      <BarraCatalogo datos="paquetes" filtro={filtro} setFiltro={setFiltro} nuevo="+ Nuevo paquete" abierta={alta} setAbierta={setAlta}
        nota={<>Los paquetes con precio salen en la tienda de la app y en el punto de venta de recepción. Foto, descripción y cómo se entrega: botón <strong>Editar</strong>.</>}>
        <div style={btnAltaCss}>
          <input placeholder="Nombre del paquete" aria-label="Nombre del paquete" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={{ ...inputCss, flex: '1 1 220px', minWidth: 0 }} autoFocus />
          <input placeholder="Sesiones" type="number" min={1} value={form.sesiones} onChange={(e) => setForm({ ...form, sesiones: e.target.value })} style={{ ...inputCss, width: 100 }} aria-label="Sesiones" />
          <input placeholder="Precio" type="number" min={0} value={form.precio} onChange={(e) => setForm({ ...form, precio: e.target.value })} style={{ ...inputCss, width: 120 }} aria-label="Precio" />
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Sale en la tienda de la app de pacientes (si tiene precio)"><input type="checkbox" checked={form.en_app} onChange={(e) => setForm({ ...form, en_app: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#8b7355' }} /> Mostrar en la app</label>
          <button className="btn-primary" onClick={crear} disabled={!form.nombre.trim()}>Agregar</button>
          <button className="btn-ghost" onClick={() => setAlta(false)}>Cancelar</button>
        </div>
      </BarraCatalogo>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando paquetes…</div>}
      {d && d.paquetes.length === 0 && <div className="card" style={{ padding: 16 }}><span className="muted" style={{ fontSize: 14 }}>Todavía no hay paquetes en esta clínica.</span></div>}
      {d && d.paquetes.length > 0 && <FilaCatalogo
        filas={visibles.map((p) => ({
          id: p.package_id, principal: p.nombre, activo: p.activo,
          extras: [`${p.sesiones} ${p.sesiones === 1 ? 'sesión' : 'sesiones'}`],
          precio: p.precio,
          app: { en_app: !!p.en_app, con_precio: !!p.con_precio, pista: pistaApp(p.app, 'paquete') },
        }))}
        totales={`${visibles.length} de ${d.paquetes.length}`}
        onApp={(id, v) => editar(id, { en_app: v })} seccionApp={d.app?.seccion}
        onNombre={(id, v) => editar(id, { nombre: v })}
        onPrecio={(id, v) => v !== null && editar(id, { precio: v })}
        onActivo={(id, v) => editar(id, { activo: v })}
        onEditarFila={(id) => { const fila = d.paquetes.find((p) => p.package_id === id); if (fila) setEditando({ fila }); }}
      />}
      {f && editando && (
        <ModalEditar titulo={`${editando.nuevo ? 'Nuevo' : 'Editar'}: ${f.nombre}`} nota={editando.nuevo ? NUEVO_NOTA : undefined} cancelar={editando.nuevo ? 'Ahora no' : undefined}
          onCerrar={() => setEditando(null)}
          campos={[
            { key: 'nombre', label: 'Nombre', valor: f.nombre },
            { key: 'precio', label: 'Precio del paquete (MXN)', valor: f.precio == null ? '' : String(f.precio), tipo: 'number' },
            { key: 'sesiones', label: 'Sesiones que incluye', valor: String(f.sesiones || 1), tipo: 'number' },
            { key: 'descripcion', label: 'Descripción (catálogo)', valor: f.descripcion || '', tipo: 'textarea' },
          ]}
          app={{ tipo: 'paquete', info: f.app, almacen: d?.app?.almacen, conPrecio: (v) => Number(v.precio) > 0, fallback: (v) => v.descripcion || null }}
          onGuardar={async (v, app) => {
            await api(`panel/paquetes/${f.package_id}` + qs, { method: 'PUT', body: JSON.stringify({
              nombre: v.nombre, precio: v.precio === '' ? undefined : Number(v.precio), sesiones: Number(v.sesiones) || 1, descripcion: v.descripcion,
              ...(app.cambios ? { app: app.cambios } : {}),
            }) });
            try { await subirFotoApp(qs, { tipo: 'paquete', ref_id: f.package_id }, app.foto); } finally { load(); }
            setMsg({ t: `Guardado: ${v.nombre}.`, tipo: 'ok' });
          }} />
      )}
    </div>
  );
}

// ============================================================= FARMACIA
interface Prod { product_id: string; producto: string; presentacion: string | null; precio_mxn: number | null; indicacion: string | null; categoria: string | null; activo: boolean; con_precio?: boolean; en_app?: boolean; app?: AppInfo }

function Farmacia({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ productos: Prod[]; app?: Seccion } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [filtro, setFiltro] = useState('');
  const [editando, setEditando] = useState<{ fila: Prod; nuevo?: boolean } | null>(null);
  const [form, setForm] = useState({ producto: '', precio_mxn: '', categoria: 'DERMOCOSMETICA', presentacion: '', en_app: true });
  const [alta, setAlta] = useState(false);

  const load = () => api<{ productos: Prod[]; app?: Seccion }>('panel/tienda' + qs).then((x) => { setD(x); return x; }).catch((e) => { setMsg({ t: e.message, tipo: 'error' }); return null; });
  useEffect(() => { setD(null); load(); }, [qs, tenantSel]);

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/tienda/${id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const crear = async () => {
    setMsg(null);
    try {
      const r = await api<{ creado: { product_id: string } }>('panel/tienda' + qs, { method: 'POST', body: JSON.stringify({ ...form, precio_mxn: form.precio_mxn ? Number(form.precio_mxn) : null }) });
      setForm({ producto: '', precio_mxn: '', categoria: form.categoria, presentacion: '', en_app: true }); setAlta(false);
      const x = await load(); const fila = x?.productos.find((p) => p.product_id === r.creado?.product_id);
      if (fila) setEditando({ fila, nuevo: true });
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };

  const visibles = (d?.productos || []).filter((p) => !filtro || p.producto.toLowerCase().includes(filtro.toLowerCase()));
  const f = editando?.fila;

  return (
    <div>
      <BarraCatalogo datos="farmacia" filtro={filtro} setFiltro={setFiltro} nuevo="+ Nuevo producto" abierta={alta} setAbierta={setAlta}
        nota={<>Dermocosmética y suplementos. Lo que tiene precio sale en la tienda de la app y por default se recoge en la clínica. Foto, descripción, destacado y cómo se entrega: botón <strong>Editar</strong>.</>}>
        <div style={btnAltaCss}>
          <input placeholder="Nombre del producto" aria-label="Nombre del producto" value={form.producto} onChange={(e) => setForm({ ...form, producto: e.target.value })} style={{ ...inputCss, flex: '1 1 220px', minWidth: 0 }} autoFocus />
          <input placeholder="Presentación" value={form.presentacion} onChange={(e) => setForm({ ...form, presentacion: e.target.value })} style={{ ...inputCss, width: 130 }} />
          <input placeholder="Precio" type="number" value={form.precio_mxn} onChange={(e) => setForm({ ...form, precio_mxn: e.target.value })} style={{ ...inputCss, width: 110 }} />
          <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} style={inputCss}>
            <option value="DERMOCOSMETICA">Dermocosmética</option>
            <option value="SUPLEMENTO">Suplemento</option>
          </select>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 13 }} title="Sale en la tienda de la app de pacientes (si tiene precio)"><input type="checkbox" checked={form.en_app} onChange={(e) => setForm({ ...form, en_app: e.target.checked })} style={{ width: 16, height: 16, accentColor: '#8b7355' }} /> Mostrar en la app</label>
          <button className="btn-primary" onClick={crear} disabled={!form.producto.trim()}>Agregar</button>
          <button className="btn-ghost" onClick={() => setAlta(false)}>Cancelar</button>
        </div>
      </BarraCatalogo>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando farmacia…</div>}
      {d && <FilaCatalogo
        filas={visibles.map((p) => ({
          id: p.product_id, principal: p.producto, activo: p.activo,
          extras: [p.categoria || '—', p.presentacion || '—'],
          precio: p.precio_mxn,
          app: p.en_app === undefined ? undefined : { en_app: !!p.en_app, con_precio: !!p.con_precio, pista: pistaApp(p.app, 'farmacia') },
        }))}
        totales={`${visibles.length} de ${d.productos.length}`}
        onApp={(id, v) => editar(id, { en_app: v })} seccionApp={d.app?.seccion}
        onNombre={(id, v) => editar(id, { producto: v })}
        onPrecio={(id, v) => editar(id, { precio_mxn: v })}
        onActivo={(id, v) => editar(id, { activo: v })}
        onEditarFila={(id) => { const fila = d.productos.find((p) => p.product_id === id); if (fila) setEditando({ fila }); }}
      />}
      {f && editando && (
        <ModalEditar titulo={`${editando.nuevo ? 'Nuevo' : 'Editar'}: ${f.producto}`} nota={editando.nuevo ? NUEVO_NOTA : undefined} cancelar={editando.nuevo ? 'Ahora no' : undefined}
          onCerrar={() => setEditando(null)}
          campos={[
            { key: 'producto', label: 'Producto', valor: f.producto },
            { key: 'presentacion', label: 'Presentación', valor: f.presentacion || '' },
            { key: 'precio_mxn', label: 'Precio (MXN)', valor: f.precio_mxn == null ? '' : String(f.precio_mxn), tipo: 'number' },
            { key: 'categoria', label: 'Categoría', valor: f.categoria || '', opciones: ['DERMOCOSMETICA', 'SUPLEMENTO', ''] },
            { key: 'indicacion', label: 'Indicación (para qué sirve)', valor: f.indicacion || '', tipo: 'textarea' },
          ]}
          app={{ tipo: 'farmacia', info: f.app, almacen: d?.app?.almacen, conPrecio: (v) => Number(v.precio_mxn) > 0,
            // Sin descripción propia, la app enseña presentación · indicación.
            fallback: (v) => [v.presentacion, v.indicacion].filter(Boolean).join(' · ') || null }}
          onGuardar={async (v, app) => {
            await api(`panel/tienda/${f.product_id}` + qs, { method: 'PUT', body: JSON.stringify({
              producto: v.producto, presentacion: v.presentacion, categoria: v.categoria, indicacion: v.indicacion,
              precio_mxn: v.precio_mxn === '' ? null : Number(v.precio_mxn),
              ...(app.cambios ? { app: app.cambios } : {}),
            }) });
            try { await subirFotoApp(qs, { tipo: 'farmacia', ref_id: f.product_id }, app.foto); } finally { load(); }
            setMsg({ t: `Guardado: ${v.producto}.`, tipo: 'ok' });
          }} />
      )}
    </div>
  );
}

// ==================================================== PRODUCTOS PROPIOS
// Lo que se vende en la app y NO está en el catálogo (el libro de la doctora). Aquí sí se pone el precio.
interface Propio { item_id: string; tipo: 'libro' | 'otro'; nombre: string; descripcion: string | null; precio: number; foto_url: string | null; con_foto: boolean; entrega: string; destacado: boolean; activo: boolean }
const TIPO_PROPIO: Record<string, string> = { libro: 'Libro', otro: 'Otro' };
const ENTREGA_LARGA: Record<string, string> = { agendar: 'Se agenda cita', recoger: 'Se recoge en la clínica', envio: 'Envío a domicilio' };

function Propios({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ propios: Propio[]; almacen?: boolean } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [editando, setEditando] = useState<Propio | 'nuevo' | null>(null);

  const load = () => api<{ propios: Propio[]; almacen?: boolean }>('panel/propios' + qs).then(setD).catch((e) => setMsg({ t: e.message, tipo: 'error' }));
  useEffect(() => { setD(null); load(); }, [qs, tenantSel]);

  const quitar = async (p: Propio) => {
    if (!(await confirmar({ titulo: '¿Quitar de la tienda?', peligro: true, confirmar: 'Quitar',
      mensaje: `"${p.nombre}" deja de venderse en la app y sale de esta lista.\nSi sólo quieres esconderlo un tiempo, desmarca «En la app».` }))) return;
    setMsg(null);
    try { await api(`panel/propios/${p.item_id}` + qs, { method: 'PUT', body: JSON.stringify({ eliminar: true }) }); setMsg({ t: `"${p.nombre}" ya no está en la tienda.`, tipo: 'ok' }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const alternar = async (p: Propio) => {
    setMsg(null);
    try { await api(`panel/propios/${p.item_id}` + qs, { method: 'PUT', body: JSON.stringify({ app: { en_app: !p.activo } }) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const e = editando === 'nuevo' ? null : editando;
  const th: React.CSSProperties = { textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' };
  const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--border)' };

  return (
    <div data-seccion="propios">
      <BarraCatalogo datos="propios" nuevo="+ Nuevo producto" abierta={false} setAbierta={() => setEditando('nuevo')}
        nota={<>Lo que se vende en la app y no está en el catálogo, como el libro de la doctora. Aquí sí se pone el precio.
          Lo del catálogo (procedimientos, paquetes, farmacia) sale solo con su precio y se edita en su pestaña.</>} />
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando…</div>}
      {d && d.propios.length === 0 && <div className="card" style={{ padding: 16 }}><span className="muted" style={{ fontSize: 14 }}>Todavía no hay productos propios. El primero que agregues aparece en la tienda de la app.</span></div>}
      {d && d.propios.length > 0 && (
        <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['', 'Producto', 'Precio', 'Entrega', 'En la app', ''].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.propios.map((p) => (
                <tr key={p.item_id} data-propio={p.nombre} style={{ opacity: p.activo ? 1 : 0.55 }}>
                  <td style={{ ...td, width: 64 }}><div style={{ width: 52, height: 40, borderRadius: 8, border: '1px solid var(--border)', background: p.foto_url ? `center / cover no-repeat url("${p.foto_url}")` : 'var(--bg-alt)' }} title={p.con_foto ? 'Con foto' : 'Sin foto'} /></td>
                  <td style={{ ...td, minWidth: 180 }}><strong>{p.nombre}</strong>{p.destacado && <span className="badge" style={{ marginLeft: 6, padding: '1px 8px', fontSize: 11 }}>destacado</span>}<div className="muted" style={{ fontSize: 12 }}>{TIPO_PROPIO[p.tipo] || p.tipo}{p.descripcion ? ` · ${p.descripcion.slice(0, 70)}${p.descripcion.length > 70 ? '…' : ''}` : ''}</div></td>
                  <td style={{ ...td, fontWeight: 700, color: 'var(--gold-dark)', whiteSpace: 'nowrap' }}>{money(p.precio)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{ENTREGA_LARGA[p.entrega] || p.entrega}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }}>
                      <input type="checkbox" checked={p.activo} onChange={() => alternar(p)} style={{ width: 16, height: 16, accentColor: '#8b7355' }} aria-label={`${p.nombre} en la app`} /> {p.activo ? 'sí' : 'oculto'}
                    </label></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }} onClick={() => setEditando(p)}>Editar</button>
                      <MasMenu etiqueta="" ariaLabel={`Más acciones de ${p.nombre}`} datos={`propio:${p.nombre}`} items={[
                        { etiqueta: 'Quitar de la tienda…', peligro: true, accion: 'quitar', onClick: () => quitar(p) },
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editando && (
        <ModalEditar titulo={e ? `Editar: ${e.nombre}` : 'Nuevo producto de la tienda'} onCerrar={() => setEditando(null)}
          campos={[
            { key: 'nombre', label: 'Nombre en la tienda', valor: e?.nombre || '' },
            { key: 'tipo', label: 'Tipo', valor: e?.tipo || 'libro', opciones: ['libro', 'otro'], etiqueta: (o) => TIPO_PROPIO[o] || o },
            { key: 'precio', label: 'Precio (MXN)', valor: e ? String(e.precio) : '', tipo: 'number' },
          ]}
          app={{ tipo: 'libro', info: e ? { item_id: e.item_id, en_app: e.activo, descripcion: e.descripcion, destacado: e.destacado, entrega: e.entrega, foto_url: e.foto_url, con_foto: e.con_foto } : undefined,
            almacen: d?.almacen, propio: true, conPrecio: (v) => v.precio !== '' && Number(v.precio) >= 0 }}
          onGuardar={async (v, app) => {
            if (!v.nombre.trim()) throw new Error('Ponle nombre.');
            if (v.precio === '' || !(Number(v.precio) >= 0)) throw new Error('Ponle precio (puede ser 0).');
            let itemId = e?.item_id;
            if (e) {
              await api(`panel/propios/${e.item_id}` + qs, { method: 'PUT', body: JSON.stringify({ nombre: v.nombre, tipo: v.tipo, precio: Number(v.precio), ...(app.cambios ? { app: app.cambios } : {}) }) });
            } else {
              const r = await api<{ item_id: string }>('panel/propios' + qs, { method: 'POST', body: JSON.stringify({ nombre: v.nombre, tipo: v.tipo, precio: Number(v.precio), app: app.cambios || {} }) });
              itemId = r.item_id;
            }
            try { if (itemId) await subirFotoApp(qs, { item_id: itemId }, app.foto); } finally { load(); }
            setMsg({ t: e ? `Guardado: ${v.nombre}.` : `"${v.nombre}" ya está en la tienda.`, tipo: 'ok' });
          }} />
      )}
    </div>
  );
}

// =========================================================== PREMIOS
interface Premio { reward_id: string; nombre: string; puntos: number; descripcion: string | null; orden: number; activo: boolean }

function Premios({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const [d, setD] = useState<{ premios?: Premio[]; sinDatos?: boolean; motivo?: string; queHacer?: string } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [form, setForm] = useState({ nombre: '', puntos: '', descripcion: '' });
  const [preparando, setPreparando] = useState(false);
  const [alta, setAlta] = useState(false);

  const load = () => api<any>('panel/premios' + qs).then(setD).catch((e) => setMsg({ t: e.message, tipo: 'error' }));
  useEffect(() => { setD(null); load(); }, [qs, tenantSel]);

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/premios/${id}` + qs, { method: 'PUT', body: JSON.stringify(cambios) }); load(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const crear = async () => {
    setMsg(null);
    try {
      await api('panel/premios' + qs, { method: 'POST', body: JSON.stringify({ nombre: form.nombre, puntos: Number(form.puntos), descripcion: form.descripcion || null }) });
      setForm({ nombre: '', puntos: '', descripcion: '' }); setAlta(false); load();
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };

  if (d?.sinDatos) return (
    <div className="card" style={{ padding: 18 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Falta preparar la tabla de premios</div>
      <div className="muted" style={{ fontSize: 14, lineHeight: 1.6 }}>
        Un clic y queda: se crea la tabla de premios (y el rol de marketing) en la base. Es seguro repetirlo.
      </div>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      <button className="btn-primary" style={{ marginTop: 12 }} disabled={preparando}
        onClick={async () => {
          setPreparando(true); setMsg(null);
          try {
            await api('migrate', { method: 'POST', body: JSON.stringify({ confirmar: 'aplicar' }) });
            setMsg({ t: 'Listo: tabla creada. Cargando…', tipo: 'ok' });
            load();
          } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
          setPreparando(false);
        }}>{preparando ? 'Preparando…' : 'Crear la tabla ahora'}</button>
    </div>
  );

  return (
    <div>
      <BarraCatalogo datos="premios" nuevo="+ Nuevo premio" abierta={alta} setAbierta={setAlta}
        nota={<>El catálogo de canjes: lo que ve el equipo médico en el popup de lealtad del expediente (DraIA), recepción al cobrar y cada paciente en su app — dorado lo que ya
          alcanza con sus puntos, gris lo que no. Cada $1,000 de compra equivale a 1 punto.</>}>
        <div style={btnAltaCss}>
          <input placeholder="Premio (ej. Limpieza facial)" aria-label="Premio" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={{ ...inputCss, flex: '1 1 200px', minWidth: 0 }} autoFocus />
          <input placeholder="Puntos" type="number" value={form.puntos} onChange={(e) => setForm({ ...form, puntos: e.target.value })} style={{ ...inputCss, width: 100 }} />
          <input placeholder="Condiciones (opcional)" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} style={{ ...inputCss, flex: '1 1 220px', minWidth: 0 }} />
          <button className="btn-primary" onClick={crear} disabled={!form.nombre.trim() || !Number(form.puntos)}>Agregar</button>
          <button className="btn-ghost" onClick={() => setAlta(false)}>Cancelar</button>
        </div>
      </BarraCatalogo>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando premios…</div>}
      {d?.premios && d.premios.length === 0 && (
        <div className="card" style={{ padding: 16 }}>
          <span className="muted" style={{ fontSize: 14 }}>Aún no hay premios. El primero que agregues aparece de inmediato en el expediente de DraIA.</span>
        </div>
      )}
      {d?.premios && d.premios.length > 0 && (
        <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Premio', 'Puntos', 'Condiciones', 'Estado', ''].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {d.premios.map((p) => (
                <tr key={p.reward_id} style={{ opacity: p.activo ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <EditableText valor={p.nombre} onSave={(v) => editar(p.reward_id, { nombre: v })} /></td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 700, color: 'var(--gold-dark)' }}>
                    <EditableText valor={String(p.puntos)} numero onSave={(v) => editar(p.reward_id, { puntos: Number(v) })} /></td>
                  <td className="muted" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    <EditableText valor={p.descripcion || ''} placeholder="—" onSave={(v) => editar(p.reward_id, { descripcion: v })} /></td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{p.activo ? 'visible' : 'oculto'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }}
                        onClick={() => editar(p.reward_id, { activo: !p.activo })}>{p.activo ? 'Ocultar' : 'Mostrar'}</button>
                      <MasMenu etiqueta="" ariaLabel={`Más acciones de ${p.nombre}`} datos={`premio:${p.nombre}`} items={[
                        { etiqueta: 'Quitar del catálogo…', peligro: true, accion: 'quitar', onClick: async () => {
                          if (!(await confirmar({ titulo: '¿Quitar este premio?', peligro: true, confirmar: 'Quitar',
                            mensaje: `"${p.nombre}" sale del catálogo de canjes: deja de verse en el expediente, en recepción y en la app.\nSi sólo quieres esconderlo un tiempo, usa «Ocultar».` }))) return;
                          editar(p.reward_id, { eliminar: true });
                        } },
                      ]} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =========================================================== CLÍNICAS
function Clinicas({ esGeneral, onCambio }: { esGeneral: boolean; onCambio: () => void }) {
  const [d, setD] = useState<{ clinicas: Clinica[] } | null>(null);
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [form, setForm] = useState({ nombre: '', domicilio: '' });
  const [alta, setAlta] = useState(false);

  const load = () => api<{ clinicas: Clinica[] }>('panel/clinicas').then(setD).catch((e) => setMsg({ t: e.message, tipo: 'error' }));
  useEffect(() => { load(); }, []);

  const editar = async (id: string, cambios: any) => {
    setMsg(null);
    try { await api(`panel/clinicas/${id}`, { method: 'PUT', body: JSON.stringify(cambios) }); load(); onCambio(); }
    catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };
  const crear = async () => {
    setMsg(null);
    try {
      await api('panel/clinicas', { method: 'POST', body: JSON.stringify(form) });
      setForm({ nombre: '', domicilio: '' }); setAlta(false); load(); onCambio();
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
  };

  return (
    <div>
      {esGeneral ? (
        <BarraCatalogo datos="clinicas" nuevo="+ Nueva clínica" abierta={alta} setAbierta={setAlta}
          nota={<>"Quitar" una clínica = <strong>desactivarla</strong>. Sus pacientes y usuarios la referencian; nada se borra.</>}>
          <div style={btnAltaCss}>
            <input placeholder="Nombre de la clínica" aria-label="Nombre de la clínica" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} style={{ ...inputCss, flex: '1 1 200px', minWidth: 0 }} autoFocus />
            <input placeholder="Domicilio (opcional)" value={form.domicilio} onChange={(e) => setForm({ ...form, domicilio: e.target.value })} style={{ ...inputCss, flex: '1 1 240px', minWidth: 0 }} />
            <button className="btn-primary" onClick={crear} disabled={!form.nombre.trim()}>Agregar clínica</button>
            <button className="btn-ghost" onClick={() => setAlta(false)}>Cancelar</button>
          </div>
        </BarraCatalogo>
      ) : (
        <div className="muted" style={{ fontSize: 13, margin: '0 2px 12px' }}>Solo el admin general puede crear o editar clínicas; aquí ves la tuya.</div>
      )}
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
      {!d && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Cargando clínicas…</div>}
      {d && (
        <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>{['Clínica', 'Domicilio', 'Usuarios', 'Pacientes', 'Estado', ''].map((h) => (
              <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>))}
            </tr></thead>
            <tbody>
              {d.clinicas.map((c) => (
                <tr key={c.tenant_id} style={{ opacity: c.activo ? 1 : 0.5 }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600 }}>
                    {esGeneral ? <EditableText valor={c.nombre} onSave={(v) => editar(c.tenant_id, { nombre: v })} /> : c.nombre}</td>
                  <td className="muted" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{c.domicilio || '—'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{c.usuarios}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{c.pacientes}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{c.activo ? 'activa' : 'inactiva'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
                    {esGeneral && (
                      <MasMenu etiqueta="" ariaLabel={`Más acciones de ${c.nombre}`} datos={`clinica:${c.nombre}`} items={[
                        c.activo ? { etiqueta: 'Desactivar clínica…', peligro: true, accion: 'desactivar', onClick: async () => {
                          if (await confirmar({ titulo: `¿Desactivar ${c.nombre}?`, peligro: true, confirmar: 'Desactivar',
                            mensaje: 'Sus cuentas ya no podrán administrarla. Nada se borra: pacientes, citas y usuarios se quedan y se puede reactivar.' })) editar(c.tenant_id, { activo: false });
                        } } : { etiqueta: 'Reactivar clínica', accion: 'reactivar', onClick: () => editar(c.tenant_id, { activo: true }) },
                      ]} />
                    )}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Modal de edición COMPLETA de una fila de catálogo: todos los campos a la
// vista, no solo el nombre/precio en línea. (La edición en línea sigue viva;
// esto existe porque "editar" debe verse como un botón, no adivinarse.)
// Con `app`, abajo va la sección "En la app de pacientes" del producto (0.28.0).
function ModalEditar({ titulo, nota, cancelar, campos, app, onGuardar, onCerrar }: {
  titulo: string;
  nota?: string;
  cancelar?: string;
  campos: { key: string; label: string; valor: string; tipo?: 'number' | 'text' | 'textarea' | 'tel'; opciones?: string[]; etiqueta?: (o: string) => string }[];
  app?: { tipo: TipoProducto; info?: AppInfo; almacen?: boolean; propio?: boolean; conPrecio: (v: Record<string, string>) => boolean; fallback?: (v: Record<string, string>) => string | null };
  onGuardar: (valores: Record<string, string>, app: GuardadoApp) => Promise<void>;
  onCerrar: () => void;
}) {
  const [v, setV] = useState<Record<string, string>>(Object.fromEntries(campos.map((c) => [c.key, c.valor])));
  const [ini] = useState<EstadoApp>(() => estadoApp(app?.info, app?.tipo || 'otro'));
  const [ea, setEa] = useState<EstadoApp>(ini);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const extra: ReactNode = app ? <SeccionApp valor={ea} onChange={setEa} conPrecio={app.conPrecio(v)} fallback={app.fallback ? app.fallback(v) : null} almacen={app.almacen} propio={app.propio} /> : null;
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(20,25,30,0.55)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" role="dialog" aria-label={titulo} data-modal={app ? 'producto' : 'editar'} style={{ padding: 18, width: `min(94vw, ${app ? 540 : 460}px)`, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ fontWeight: 700, color: 'var(--gold-dark)', marginBottom: nota ? 4 : 12 }}>{titulo}</div>
        {nota && <div className="muted" style={{ fontSize: 12.5, marginBottom: 12 }}>{nota}</div>}
        {campos.map((c) => (
          <div key={c.key} style={{ marginBottom: 10 }}>
            <label className="muted" style={{ fontSize: 12, display: 'block', marginBottom: 3 }} htmlFor={`campo-${c.key}`}>{c.label}</label>
            {c.opciones ? (
              <select id={`campo-${c.key}`} value={v[c.key]} onChange={(e) => setV({ ...v, [c.key]: e.target.value })} style={{ ...inputCss, width: '100%' }}>
                {c.opciones.map((o) => <option key={o} value={o}>{c.etiqueta ? c.etiqueta(o) : o || '(sin categoría)'}</option>)}
              </select>
            ) : c.tipo === 'textarea' ? (
              <textarea id={`campo-${c.key}`} value={v[c.key]} onChange={(e) => setV({ ...v, [c.key]: e.target.value })} rows={3} style={{ ...inputCss, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
            ) : (
              <input id={`campo-${c.key}`} type={c.tipo || 'text'} value={v[c.key]} onChange={(e) => setV({ ...v, [c.key]: e.target.value })} style={{ ...inputCss, width: '100%' }} />
            )}
          </div>
        ))}
        {extra}
        {err && <Aviso texto={err} tipo="error" />}
        {/* Pegado abajo: con la sección de la app el diálogo es largo y en celular "Guardar" quedaba fuera de vista. */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap', position: 'sticky', bottom: -18, zIndex: 1, background: 'var(--card)', margin: '14px -18px -18px', padding: '12px 18px 16px', borderTop: app ? '1px solid var(--border)' : undefined }}>
          <button className="btn-ghost" onClick={onCerrar} disabled={busy}>{cancelar || 'Cancelar'}</button>
          <button className="btn-primary" disabled={busy} data-guardar
            onClick={async () => {
              setBusy(true); setErr(null);
              try { await onGuardar(v, { cambios: app ? cambiosApp(ini, ea) : null, foto: app ? ea.foto_nueva : null }); onCerrar(); }
              catch (e: any) { setErr(e.message); setBusy(false); }
            }}>
            {busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================== piezas compartidas
function EditableText({ valor, onSave, numero, placeholder }: { valor: string; onSave: (v: string) => void; numero?: boolean; placeholder?: string }) {
  const [v, setV] = useState(valor);
  useEffect(() => { setV(valor); }, [valor]);
  return (
    <input value={v} type={numero ? 'number' : 'text'} placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== valor && v.trim()) onSave(v.trim()); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      style={{ border: 'none', borderBottom: '1px dashed var(--border)', background: 'transparent', fontFamily: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit', width: numero ? 92 : '100%', minWidth: 60, padding: '2px 0' }} />
  );
}

/**
 * Arriba de cada catálogo: el buscador y UNA acción principal ("+ Nuevo …"). El alta se abre al tocarla (antes el
 * formulario de alta estaba siempre a la vista, aunque casi siempre se viene a buscar o editar) y la explicación larga
 * va plegada en "¿Cómo funciona?".
 */
function BarraCatalogo({ filtro, setFiltro, nuevo, abierta, setAbierta, nota, datos, children }: {
  filtro?: string; setFiltro?: (v: string) => void; nuevo?: string; abierta: boolean; setAbierta: (v: boolean) => void;
  nota?: ReactNode; datos: string; children?: ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 12, marginBottom: 12 }} data-barra-catalogo={datos}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {setFiltro && <input placeholder="Buscar…" aria-label="Buscar" value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ ...inputCss, flex: '1 1 200px', minWidth: 0 }} />}
        {nuevo && !abierta && <button className="btn-primary" style={{ marginLeft: 'auto', padding: '8px 16px' }} onClick={() => setAbierta(true)} data-nueva={datos}>{nuevo}</button>}
      </div>
      {abierta && <div data-alta={datos} style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>{children}</div>}
      {nota && <details className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}><summary style={{ cursor: 'pointer' }}>¿Cómo funciona?</summary><div style={{ marginTop: 4 }}>{nota}</div></details>}
    </div>
  );
}
const btnAltaCss: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' };

function FilaCatalogo({ filas, totales, onNombre, onPrecio, onActivo, onEditarFila, onApp, seccionApp }: {
  filas: { id: string; principal: string; activo: boolean; extras: string[]; precio: number | null; app?: { en_app: boolean; con_precio: boolean; pista?: string } }[];
  totales: string;
  /** "En la app": la tienda de la app de la paciente enseña el catálogo con precio; esto la oculta o la vuelve a enseñar. */
  onApp?: (id: string, v: boolean) => void;
  seccionApp?: boolean;
  onNombre: (id: string, v: string) => void;
  onPrecio: (id: string, v: number | null) => void;
  onActivo: (id: string, v: boolean) => void;
  onEditarFila?: (id: string) => void;
}) {
  return (
    <>
      <div className="muted" style={{ fontSize: 12, margin: '0 2px 8px' }}>{totales}</div>
      <div className="card tabla-scroll" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Nombre', 'Detalle', 'Precio', ...(onApp ? ['En la app'] : []), 'Estado', ''].map((h) => (
            <th key={h} style={{ textAlign: 'left', padding: '9px 12px', borderBottom: '2px solid var(--border)', color: 'var(--ink-soft)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>))}
          </tr></thead>
          <tbody>
            {filas.map((f) => (
              <tr key={f.id} data-fila={f.principal} style={{ opacity: f.activo ? 1 : 0.5 }}>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600, minWidth: 200 }}>
                  <EditableText valor={f.principal} onSave={(v) => onNombre(f.id, v)} /></td>
                <td className="muted" style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontSize: 12 }}>{f.extras.join(' · ')}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', fontWeight: 700, color: 'var(--gold-dark)', whiteSpace: 'nowrap' }}>
                  <EditableText valor={f.precio == null ? '' : String(f.precio)} numero placeholder="—"
                    onSave={(v) => onPrecio(f.id, v === '' ? null : Number(v))} /></td>
                {onApp && f.app && (
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }} data-app={f.principal}>
                    {!f.app.con_precio ? <span className="muted" style={{ fontSize: 12 }} title="Lo que no tiene precio no sale en la tienda de la app">sin precio</span> : (
                      <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5, cursor: 'pointer' }} title={seccionApp === false ? 'Esta sección de la tienda está apagada (App de pacientes → Tienda)' : 'Se ve en la tienda de la app de pacientes. Foto, descripción y entrega: botón Editar.'}>
                        <input type="checkbox" checked={f.app.en_app} disabled={!f.activo} onChange={(e) => onApp(f.id, e.target.checked)} style={{ width: 16, height: 16, accentColor: '#8b7355' }} />
                        {f.app.en_app ? (seccionApp === false ? 'sí (sección apagada)' : 'sí') : 'oculto'}
                      </label>)}
                    {f.app.con_precio && f.app.en_app && f.app.pista && <div className="muted" style={{ fontSize: 11, whiteSpace: 'normal', marginTop: 2 }}>{f.app.pista}</div>}
                  </td>
                )}
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>{f.activo ? 'activo' : 'inactivo'}</td>
                <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {onEditarFila && <button className="btn-ghost" style={{ fontSize: 12, padding: '3px 10px' }}
                      onClick={() => onEditarFila(f.id)}>Editar</button>}
                    <MasMenu etiqueta="" ariaLabel={`Más acciones de ${f.principal}`} datos={`fila:${f.principal}`} items={[
                      f.activo ? { etiqueta: 'Desactivar…', peligro: true, accion: 'desactivar', onClick: async () => {
                        if (await confirmar({ titulo: `¿Desactivar ${f.principal}?`, peligro: true, confirmar: 'Desactivar',
                          mensaje: 'Deja de ofrecerse en recepción y en la app de pacientes. No se borra: se puede reactivar.' })) onActivo(f.id, false);
                      } } : { etiqueta: 'Reactivar', accion: 'reactivar', onClick: () => onActivo(f.id, true) },
                    ]} />
                  </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}


// ============================================================== PREFERENCIAS
/**
 * Lo que la clínica elige UNA vez para todas las pantallas (API `GET|PUT preferencias`, lib/preferencias.ts). Hoy:
 * con qué periodo abren las vistas con fechas. 30 días por default (lo pidió el dueño); cada vista lo sigue cambiando
 * en el momento con su selector.
 */
const PERIODOS_PREF: { key: string; label: string; nota?: string }[] = [
  { key: 'hoy', label: 'Hoy' }, { key: '7d', label: '7 días' }, { key: '30d', label: '30 días', nota: 'recomendado' },
  { key: 'mes', label: 'Este mes' }, { key: 'mes_pasado', label: 'Mes pasado' }, { key: '90d', label: '90 días' },
  { key: 'anio', label: 'Este año' }, { key: 'todo', label: 'Todo', nota: 'puede tardar más' },
];
const VISTAS_CON_FECHAS = 'Resumen, Alertas, Analítica, Marketing, Comisiones, Equipo, Formularios web y, en App de pacientes, Referidos, Mensajes y avisos, Pedidos, Tarjetas de regalo y Lo que genera';

function PreferenciasClinica({ qs, tenantSel }: { qs: string; tenantSel: string }) {
  const { setPreferencias } = useAuth();
  const [d, setD] = useState<{ config: { periodo_default: string }; guardada: boolean; updated_at: string | null; updated_by: string | null } | null>(null);
  const [periodo, setPeriodo] = useState('30d');
  const [msg, setMsg] = useState<{ t: string; tipo: 'ok' | 'error' } | null>(null);
  const [busy, setBusy] = useState(false);
  const leer = (r: any) => { setD(r); setPeriodo(r.config?.periodo_default || '30d'); };
  useEffect(() => { setD(null); setMsg(null); api<any>('preferencias' + qs).then(leer).catch((e) => setMsg({ t: e.message, tipo: 'error' })); }, [qs, tenantSel]);
  const guardar = async () => {
    setBusy(true); setMsg(null);
    try {
      leer(await api<any>('preferencias' + qs, { method: 'PUT', body: JSON.stringify({ periodo_default: periodo }) }));
      // Lo de esta sesión: las vistas que se abran desde ahora ya arrancan con lo nuevo (las de otras personas, al
      // abrir su siguiente vista o al volver a entrar).
      api<any>('preferencias').then((r) => setPreferencias(r.config)).catch(() => {});
      setMsg({ t: 'Guardado. Las vistas abren con este periodo desde ahora; en cada una se puede cambiar en el momento.', tipo: 'ok' });
    } catch (e: any) { setMsg({ t: e.message, tipo: 'error' }); }
    setBusy(false);
  };
  if (!d) return msg ? <Aviso texto={msg.t} tipo={msg.tipo} /> : <p className="muted">Cargando…</p>;
  const cambio = periodo !== d.config.periodo_default;
  return (
    <div className="card" style={{ padding: 16, maxWidth: 760 }} data-seccion="preferencias">
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Periodo con el que abren las vistas</div>
      <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
        Con qué fechas arranca cada vista con números: {VISTAS_CON_FECHAS}. En cada una se puede cambiar en el momento con su
        selector; esto sólo decide con qué abre, para toda la clínica.
      </p>
      <div role="radiogroup" aria-label="Periodo con el que abren las vistas" style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '10px 0' }}>
        {PERIODOS_PREF.map((p) => (
          <button key={p.key} type="button" role="radio" aria-checked={periodo === p.key} data-periodo-default={p.key} className="btn-ghost"
            onClick={() => setPeriodo(p.key)}
            style={{ padding: '6px 13px', fontSize: 13, boxShadow: 'none', ...(periodo === p.key ? { borderColor: 'var(--gold)', background: 'var(--gold-light)', color: 'var(--gold-dark)', fontWeight: 700 } : {}) }}>
            {p.label}{p.nota ? <span className="muted" style={{ fontWeight: 400, fontSize: 11.5 }}> · {p.nota}</span> : null}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn-primary" onClick={guardar} disabled={busy || !cambio} data-guardar-preferencias>{busy ? 'Guardando…' : 'Guardar'}</button>
        <span className="muted" style={{ fontSize: 12 }}>
          {d.guardada ? `Último cambio: ${d.updated_at ? new Date(d.updated_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : ''}${d.updated_by ? ' · ' + d.updated_by : ''}` : 'Todavía no se ha elegido: las vistas abren con 30 días.'}
        </span>
      </div>
      {msg && <Aviso texto={msg.t} tipo={msg.tipo} />}
    </div>
  );
}
